// File: src/pages/seller/AddProduct/sections/BidsInformation.jsx
import React from "react";
import { useFormContext, Controller } from "react-hook-form";
import {
  CRow,
  CCol,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CFormCheck,
} from "@coreui/react";
import {
  formatWithCommasAndDecimals,
  parseRawNumber,
} from "../../../../utils/price";

const BidsInformation = ({ isMetal = true }) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const isReverseAuction = watch("is_reverse_auction");
  const allowBidding = watch("allow_bidding");

  const calcUnit = watch("calc_unit");
  const currency = watch("currency");
  const pricePerUnit = watch("price_per_unit");

  const getDynamicLabel = (baseLabel, showPricePerUnit = false) => {
    if (!calcUnit || !currency) return baseLabel;
    const ratePart =
      showPricePerUnit && pricePerUnit
        ? `Price per unit: ${formatWithCommasAndDecimals(pricePerUnit)} `
        : "";
    return (
      <>
        {baseLabel}
        <span
          className="text-muted ms-2"
          style={{ fontWeight: "400", fontSize: "0.85rem" }}
        >
          ({ratePart}
          {currency}/{calcUnit})
        </span>
      </>
    );
  };

  const getCurrentDateTime = () => {
  const now = new Date();
  // Adjusts to local time string and removes seconds/milliseconds
  return now.toISOString().slice(0, 16); 
};

  return (
    <div
      style={{
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        padding: "1.5rem",
        position: "relative",
        marginBottom: "1.5rem",
      }}
    >
      {/* Section Title */}
      <span
        style={{
          position: "absolute",
          top: "-0.8rem",
          left: "1rem",
          backgroundColor: "white",
          padding: "0 0.5rem",
          fontWeight: "600",
          color: "#333",
          fontSize: "1rem",
        }}
      >
        Bids Information
      </span>

      <CRow className="mb-3">
        <CFormLabel>Listing Type</CFormLabel>
        <div style={{ display: "flex", flexDirection: "row", gap: "0.50rem" }}>
          <Controller
            name="allow_bidding"
            control={control}
            style={{ flexDirection: "column" }}
            render={({ field }) => (
              <>
                <CFormCheck
                  type="radio"
                  id="create-bid"
                  label="Create Bid"
                  value={1}
                  checked={field.value === 1}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
                <CFormCheck
                  type="radio"
                  id="sell-product"
                  label="Sell as Product"
                  value={0}
                  checked={field.value === 0}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              </>
            )}
          />
        </div>
      </CRow>

      {/* Commented out for future use: Reverse Auction fields */}
      {/* 
      <CRow className="mb-3">
        <CCol md={12}>
          <div className="d-flex align-items-center gap-2">
            <CFormSwitch
              id="is_reverse_auction"
              label="Is Reverse Auction?"
              {...register("is_reverse_auction")}
            />
          </div>
        </CCol>
      </CRow>

      {isReverseAuction && (
        <CRow className="mb-3">
          <CCol md={4}>
            <CFormLabel>Reverse Close Price</CFormLabel>
            <Controller
              name="reverse_close_price"
              control={control}
              render={({ field }) => {
                const formattedValue = field.value
                  ? formatWithCommasAndDecimals(field.value)
                  : "";
                return (
                  <CFormInput
                    {...field}
                    type="text"
                    value={formattedValue}
                    placeholder="Enter reverse close price"
                    onChange={(e) => {
                      const raw = parseRawNumber(e.target.value);
                      field.onChange(raw);
                    }}
                  />
                );
              }}
            />
          </CCol>
        </CRow>
      )}
      */}

      {!isMetal && (
        <>
          <CRow className="mb-3">
            {/* Unit */}
            <CCol md={3}>
              <CFormLabel>Unit</CFormLabel>
              <Controller
                name="calc_unit"
                control={control}
                render={({ field }) => (
                  <CFormSelect
                    {...field}
                    options={[
                      { label: "Select", value: "" },
                      { label: "Troy Ounce", value: "toz" },
                      { label: "Metric Tonne", value: "mt" },
                      { label: "Kg", value: "kg" },
                      { label: "gram", value: "g" },
                    ]}
                  />
                )}
              />
              {errors.calc_unit && (
                <div style={{ color: "red", marginTop: "4px" }}>
                  {errors.calc_unit.message}
                </div>
              )}
            </CCol>

            {/* Currency */}
            <CCol md={3}>
              <CFormLabel>Currency</CFormLabel>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <CFormSelect
                    {...register("currency")}
                  >
                    <option value="">Select Currency</option>
                    <option value="AUD">AUD</option>
                    <option value="USD">USD</option>
                  </CFormSelect>
                )}
              />

              {errors.currency && (
                <div style={{ color: "red", marginTop: "4px" }}>
                  {errors.currency.message}
                </div>
              )}
            </CCol>

            {/* Estimated Price */}
            <CCol md={3}>
              <CFormLabel>{getDynamicLabel("Estimated Price")}</CFormLabel>
              <Controller
                name="estimated_price"
                control={control}
                render={({ field }) => {
                  const formattedValue = field.value
                    ? formatWithCommasAndDecimals(field.value)
                    : "";
                  return (
                    <CFormInput
                      {...field}
                      type="text"
                      value={formattedValue}
                      placeholder="Enter estimated price"
                      onChange={(e) => {
                        const raw = parseRawNumber(e.target.value);
                        field.onChange(raw);
                      }}
                    />
                  );
                }}
              />
            </CCol>

            {/* Price per Unit */}
            <CCol md={3}>
              <CFormLabel>{getDynamicLabel("Price per Unit")}</CFormLabel>
              <Controller
                name="price_per_unit"
                control={control}
                render={({ field }) => {
                  const formattedValue = field.value
                    ? formatWithCommasAndDecimals(field.value)
                    : "";
                  return (
                    <CFormInput
                      {...field}
                      type="text"
                      value={formattedValue}
                      placeholder="Enter price per unit"
                      onChange={(e) => {
                        const raw = parseRawNumber(e.target.value);
                        field.onChange(raw);
                      }}
                    />
                  );
                }}
              />
            </CCol>
          </CRow>

          <CRow className="mb-3">
            {/* Price (AUD) */}
            <CCol md={12}>
              <CFormLabel>{getDynamicLabel("Price", true)}</CFormLabel>
              <Controller
                name="price"
                control={control}
                render={({ field }) => {
                  const formattedValue = field.value
                    ? formatWithCommasAndDecimals(field.value)
                    : "";
                  return (
                    <CFormInput
                      {...field}
                      value={formattedValue}
                      onChange={(e) => {
                        const raw = parseRawNumber(e.target.value);
                        field.onChange(raw);
                      }}
                      placeholder="Enter price"
                    />
                  );
                }}
              />
              {errors.price && (
                <div style={{ color: "red", marginTop: "4px" }}>
                  {errors.price.message}
                </div>
              )}
            </CCol>
          </CRow>
          <hr className="my-4" />
        </>
      )}

      <CRow className="mb-3">
        {/* Minimum Asking Price */}
        <CCol md={3}>
          <CFormLabel>{getDynamicLabel("Minimum Asking Price ")}</CFormLabel>
          <Controller
            name="minimum_asking_price"
            control={control}
            render={({ field }) => {
              const formattedValue = field.value
                ? formatWithCommasAndDecimals(field.value)
                : "";

              return (
                <CFormInput
                  {...field}
                  type="text"
                  value={formattedValue}
                  onChange={(e) => {
                    const raw = parseRawNumber(e.target.value);
                    field.onChange(raw);
                  }}
                  disabled={allowBidding !== 1}
                  placeholder="Enter minimum asking price"
                />
              );
            }}
          />
          {errors.minimum_asking_price && (
            <div style={{ color: "red" }}>
              {errors.minimum_asking_price.message}
            </div>
          )}
        </CCol>

        {/* Bid Increment */}
        <CCol md={3}>
          <CFormLabel>{getDynamicLabel("Bid Increment")}</CFormLabel>
          <Controller
            name="bid_increment"
            control={control}
            render={({ field }) => {
              const formattedValue = field.value
                ? formatWithCommasAndDecimals(field.value)
                : "";

              return (
                <CFormInput
                  {...field}
                  type="text"
                  value={formattedValue}
                  onChange={(e) => {
                    const raw = parseRawNumber(e.target.value);
                    field.onChange(raw);
                  }}
                  disabled={allowBidding !== 1}
                  placeholder="Enter bid increment"
                />
              );
            }}
          />
          {errors.bid_increment && (
            <div style={{ color: "red" }}>{errors.bid_increment.message}</div>
          )}
        </CCol>

        {/* Bid Closed At */}
        <CCol md={3}>
          <CFormLabel>Bid Closing Date</CFormLabel>
          <CFormInput
            type="datetime-local"
            min={getCurrentDateTime()}
            {...register("bid_closed_at")}
            disabled={allowBidding !== 1}
          />
          {errors.bid_closed_at && (
            <div style={{ color: "red" }}>{errors.bid_closed_at.message}</div>
          )}
        </CCol>

        {/* Auction Status */}
        <CCol md={3}>
          <CFormLabel>Auction Status</CFormLabel>
          <Controller
            name="auction_status"
            control={control}
            disabled={allowBidding !== 1}
            render={({ field }) => (
              <CFormSelect
                {...field}
                options={[
                  { label: "Open", value: "open" },
                  // { label: "Expired", value: "expired" },
                  // { label: "Sold", value: "sold" },
                  // { label: "Closed", value: "closed" },
                ]}
                disabled
              />
            )}
          />
        </CCol>
      </CRow>
    </div>
  );
};

export default React.memo(BidsInformation);
